// CONNECTIONS-AREA: Team › Connections › Agent keys (connections design §B, §C). The keys that let
// agents INTO CompanyOS, the opposite direction of the other tabs: every DE's ingest keys (Create
// key: the DE page's shown-once dialog; Revoke behind a confirm), its agent key (handed to its
// container by the deploy, never shown, rotated there), service keys, a person's time-source keys;
// Claude Code pairings stay on their own page (a link); the Recall service key is one locked row.
// One call on open (`agentKeys.list`); the DE picker reads the cached DE list. Managers only.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  buttonVariants,
  cn,
  InfoTip,
  ListRow,
  NativeSelect,
  NativeSelectOption,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BanIcon, KeyRoundIcon, LockIcon, TerminalIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { CreateKeyDialog } from "../de/keys-tab";
import { formatDateTime } from "../format";
import { digitalEmployeesQuery, keys as queryKeys } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowActions } from "../settings/row-actions";
import { useAllPages } from "./grants";

export type CompanyAgentKey = OperationOutput<"agentKeys.list">["data"][number];

export const agentKeysKey = (co: string) => ["company", co, "agent-keys"] as const;

export const agentKeysQuery = (api: Api, co: string) => ({
  queryKey: agentKeysKey(co),
  queryFn: () => api.client.agentKeys.list({ params: { co } }),
});

const KIND_LABEL: Record<CompanyAgentKey["kind"], string> = {
  ingest: "Ingest",
  agent: "Agent",
  service: "Service",
};

const KIND_INFO: Record<CompanyAgentKey["kind"], string> = {
  ingest: "Posts activity, outcomes and costs for its DE. Shown once when created.",
  agent:
    "The DE's own key, handed to its container by the deploy. Never shown; the deploy rotates it.",
  service: "A runtime service's key (no seat). Never shown.",
};

export function AgentKeysTab({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(agentKeysQuery(api, co));
  // Every page: the picker must reach the 101st DE (Codex r1)
  const des = useAllPages(useInfiniteQuery(digitalEmployeesQuery(api, co)));
  const pickId = useId();
  const [pick, setPick] = useState("");
  const deRows = (des.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (d) => d.status !== "retired",
  );
  const chosen = deRows.find((d) => d.membership_id === pick) ?? deRows[0] ?? null;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: agentKeysKey(co) });
    if (chosen) void qc.invalidateQueries({ queryKey: queryKeys.de(co, chosen.membership_id) });
  };
  const revoke = useMutation({
    mutationFn: (k: CompanyAgentKey) =>
      api.client.digitalEmployees.revokeKey({
        params: { co, id: k.membership_id, kid: k.id },
      }),
    onSuccess: (k) => {
      void qc.invalidateQueries({ queryKey: agentKeysKey(co) });
      void qc.invalidateQueries({ queryKey: queryKeys.de(co, k.membership_id) });
      toast.add({ title: `Key ${k.key_prefix}… revoked.`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "The key was not revoked."), type: "error" }),
  });
  const rows = q.data?.data ?? [];
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="agent-keys">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-1 text-sm font-medium">
          Keys into CompanyOS
          <InfoTip label="About agent keys">
            These are how your digital employees reach CompanyOS. The other tabs are how they reach
            outside tools. Only a key's prefix is kept.
          </InfoTip>
        </h2>
        {deRows.length ? (
          <span className="ml-auto flex items-center gap-2">
            <label htmlFor={pickId} className="sr-only">
              Key for
            </label>
            <NativeSelect
              id={pickId}
              size="sm"
              value={chosen?.membership_id ?? ""}
              onChange={(e) => setPick(e.target.value)}
            >
              {deRows.map((d) => (
                <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                  {workerName(d.display_name)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {chosen ? (
              <CreateKeyDialog
                key={chosen.membership_id}
                api={api}
                companyId={co}
                de={chosen}
                onCreated={refresh}
              />
            ) : null}
          </span>
        ) : null}
      </div>
      {q.isPending ? (
        <Loading label="Loading agent keys" rows={2} />
      ) : q.isError ? (
        <LoadError what="Agent keys" error={q.error} />
      ) : (
        <ul aria-label="Agent keys" className="rounded-lg border border-border">
          {rows.map((k) => {
            const live = k.status === "active";
            const de = k.holder.kind === "digital_employee";
            return (
              <li key={k.id}>
                <ListRow
                  data-agent-key={k.id}
                  data-kind={k.kind}
                  done={!live}
                  lead={
                    <KeyRoundIcon aria-hidden="true" className="size-4 text-muted-foreground" />
                  }
                  title={
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate">
                        {de ? workerName(k.holder.display_name) : k.holder.display_name}
                      </span>
                      {de ? <WorkerBadge kind="de" size="sm" /> : null}
                    </span>
                  }
                  chips={
                    <>
                      <Badge variant="outline" title={KIND_INFO[k.kind]}>
                        {k.source ? "Time source" : KIND_LABEL[k.kind]}
                      </Badge>
                      <Badge variant={live ? "success" : "neutral"}>
                        {live ? "Active" : "Revoked"}
                      </Badge>
                      {k.scopes.slice(0, 3).map((s) => (
                        <Badge key={s} variant="neutral" className="max-md:hidden">
                          {s.replace(/^ingest:/, "")}
                        </Badge>
                      ))}
                    </>
                  }
                  meta={
                    <span className="flex items-center gap-3 max-md:hidden">
                      <span className="font-mono">{k.key_prefix}…</span>
                      <span>
                        {k.last_used_at ? `used ${formatDateTime(k.last_used_at)}` : "never used"}
                      </span>
                      {k.expires_at && live ? (
                        <span>expires {formatDateTime(k.expires_at)}</span>
                      ) : null}
                    </span>
                  }
                  actions={
                    live && k.kind === "ingest" && de ? (
                      <RowActions>
                        <ConfirmRowAction
                          icon={BanIcon}
                          label={`Revoke key ${k.key_prefix}`}
                          tip="Revoke"
                          title={`Revoke key ${k.key_prefix}…?`}
                          description={`${workerName(k.holder.display_name)}'s runtime can no longer post with this key. This cannot be undone.`}
                          confirmLabel="Revoke"
                          disabled={revoke.isPending}
                          onConfirm={() => revoke.mutate(k)}
                        />
                      </RowActions>
                    ) : undefined
                  }
                />
              </li>
            );
          })}
          <li>
            <ListRow
              data-agent-key="claude-code"
              lead={<TerminalIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
              title="Claude Code pairings"
              meta="Workforce Control tokens"
              trailing={
                <Link
                  to="/c/$slug/setup/claude-code"
                  params={{ slug: company.slug }}
                  search={keepFocus}
                  className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
                >
                  Open
                </Link>
              }
            />
          </li>
          <li>
            <ListRow
              data-agent-key="recall"
              lead={<LockIcon aria-hidden="true" className="size-4 text-muted-foreground" />}
              title="Recall (platform)"
              chips={<Badge variant="neutral">Locked</Badge>}
              meta={
                <span className="inline-flex items-center gap-1">
                  Service key
                  <InfoTip label="About the Recall key">
                    CompanyOS's own key to your company's memory. The platform holds and rotates it;
                    it is never shown.
                  </InfoTip>
                </span>
              }
            />
          </li>
        </ul>
      )}
    </div>
  );
}
